import { Button, Card, Form, ModalFooter, Page } from 'components';
import ConditionalSpinner from 'components/ConditionalSpinner/ConditionalSpinner';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { BsCheck, BsClipboard } from 'react-icons/bs';
import { useFetchWidget, useUpdateWidget } from 'api/widgets/hooks';
import Skeleton from 'react-loading-skeleton';
import { WIDGETS } from 'constants/routes';
import WIDGET_ICONS from './constants/widgetIcons';
import WIDGET_TYPES from './widgets';
import * as Ui from './WidgetsPage.styles';

const WidgetEditPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });
  const { t: tApp } = useTranslation('app');
  const previewRef = useRef(null);
  const formRef = useRef(null);

  const [formValues, setFormValues] = useState({});
  const [copied, setCopied] = useState(false);

  const { data: widget, isLoading } = useFetchWidget(id);
  const { mutate: updateWidget, isPending } = useUpdateWidget();

  const [iFrameKey, setIFrameKey] = useState(0);
  const [widgetTypeKey, setWidgetTypeKey] = useState(null);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [pendingTypeKey, setPendingTypeKey] = useState(null);

  const activeWidget = widgetTypeKey ? WIDGET_TYPES[widgetTypeKey] : null;
  const widgetUrl = widget?.short_url || '';

  useEffect(() => {
    if (widget) {
      setWidgetTypeKey(widget.type || null);
    }
  }, [widget]);

  const handleOpenTypeModal = () => {
    setPendingTypeKey(widgetTypeKey);
    setIsTypeModalOpen(true);
  };

  const handleConfirmType = () => {
    if (pendingTypeKey !== widgetTypeKey) {
      const newWidget = WIDGET_TYPES[pendingTypeKey];
      if (newWidget?.defaultValues && formRef.current) {
        Object.entries(newWidget.defaultValues).forEach(([key, value]) => {
          formRef.current.setFieldValue(key, value);
        });
      }
    }
    setWidgetTypeKey(pendingTypeKey);
    setIsTypeModalOpen(false);
  };

  const handleCloseTypeModal = () => {
    setIsTypeModalOpen(false);
    setPendingTypeKey(null);
  };

  useEffect(() => {
    const handleMessage = (e) => {
      if (
        previewRef.current &&
        e.source === previewRef.current.contentWindow &&
        e.data &&
        e.data.type === 'rambla-widget-resize'
      ) {
        previewRef.current.style.height = `${e.data.height}px`;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!formRef.current || !activeWidget) return;
    const { values } = formRef.current;
    if (!values.name?.trim() || !activeWidget.isValid(values)) return;

    updateWidget({
      uid: id,
      name: values.name,
      type: widgetTypeKey,
      ...activeWidget.buildPayload(values),
    });

    setIFrameKey((prev) => prev + 1);
  }, [activeWidget, id, widgetTypeKey, updateWidget]);

  const initialValues = useMemo(() => {
    if (!widget || !activeWidget) return { name: '' };
    return {
      name: widget.name || '',
      ...activeWidget.parseApiData(widget),
    };
  }, [widget, activeWidget]);

  const isFormValid =
    formRef.current?.dirty && !!formValues.name?.trim() && activeWidget?.isValid(formValues);
  const ActiveIcon = widgetTypeKey ? WIDGET_ICONS[widgetTypeKey] : null;
  const ConfigComponent = activeWidget?.ConfigComponent;

  const snippetCode = widgetUrl
    ? [
        `<iframe`,
        `  id="rambla-widget-${id}"`,
        `  src="${widgetUrl}"`,
        `  style="width: 100%; border: none;"`,
        `  scrolling="no"`,
        `></iframe>`,
        ``,
        `<script>`,
        `  window.addEventListener("message", function (e) {`,
        `    if (e.data && e.data.type === "rambla-widget-resize") {`,
        `      var frame = document.getElementById("rambla-widget-${id}");`,
        `      if (frame) frame.style.height = e.data.height + "px";`,
        `    }`,
        `  });`,
        `</script>`,
      ].join('\n')
    : '';

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(snippetCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [snippetCode]);

  return (
    <Page
      title={t('editTitle')}
      breadcrumbs={[
        { label: tApp('breadcrumbs.widgets'), to: WIDGETS },
        {
          label: isLoading ? <Skeleton width={120} /> : widget?.name || tApp('breadcrumbs.widget'),
        },
      ]}
      description={t('editDescription')}
      withBackButton={false}
      loading={isLoading}
      footer={{
        component: (
          <Ui.FooterWrapper>
            <Button
              size="lg"
              variant="dark"
              className="rounded"
              onClick={handleSubmit}
              disabled={!isFormValid || isPending}
            >
              <ConditionalSpinner showSpinner={isPending}>{t('save')}</ConditionalSpinner>
            </Button>
          </Ui.FooterWrapper>
        ),
        visible: !!activeWidget,
      }}
    >
      <Form initialValues={initialValues} onSubmit={() => {}} renderDefaultButtons={false}>
        {(formikProps) => {
          formRef.current = formikProps;
          setFormValues(formikProps.values);
          return (
            <Ui.ContentWrapper>
              <Card
                title={t('widgetDetails')}
                subtitle={t('widgetDetailsSubtitle')}
                className="w-100"
                style={{ marginTop: 24 }}
              >
                <Form.GroupHorizontal label={t('widgetName')} required>
                  <Form.Input
                    name="name"
                    placeholder={t('widgetNamePlaceholder')}
                    validate={(value) => (!value?.trim() ? tApp('required') : undefined)}
                  />
                </Form.GroupHorizontal>
                <Form.GroupHorizontal label={t('widgetType')} required style={{ marginTop: 16 }}>
                  <Ui.TypeSelectorCard
                    role="button"
                    tabIndex={0}
                    onClick={handleOpenTypeModal}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenTypeModal();
                      }
                    }}
                  >
                    {activeWidget ? (
                      <>
                        <Ui.TypeSelectorIcon>
                          {ActiveIcon && <ActiveIcon size={28} />}
                        </Ui.TypeSelectorIcon>
                        <Ui.TypeSelectorInfo>
                          <Ui.TypeSelectorLabel>{t(activeWidget.titleKey)}</Ui.TypeSelectorLabel>
                          <Ui.TypeSelectorDescription>
                            {t(activeWidget.descriptionKey)}
                          </Ui.TypeSelectorDescription>
                        </Ui.TypeSelectorInfo>
                        <Ui.TypeSelectorChangeHint>{t('changeType')}</Ui.TypeSelectorChangeHint>
                      </>
                    ) : (
                      <Ui.TypeSelectorPlaceholder>
                        {t('selectWidgetType')}
                      </Ui.TypeSelectorPlaceholder>
                    )}
                  </Ui.TypeSelectorCard>
                </Form.GroupHorizontal>
              </Card>

              {ConfigComponent && <ConfigComponent />}

              {snippetCode && (
                <Card
                  title={t('codeSnippet')}
                  subtitle={t('codeSnippetSubtitle')}
                  className="w-100"
                >
                  <Ui.SnippetWrapper>
                    <Ui.SnippetCode>{snippetCode}</Ui.SnippetCode>
                    <Ui.SnippetCopyButton onClick={handleCopy}>
                      {copied ? <BsCheck size={18} /> : <BsClipboard size={16} />}
                      {copied ? t('copied') : t('copy')}
                    </Ui.SnippetCopyButton>
                  </Ui.SnippetWrapper>
                </Card>
              )}

              {widgetUrl && (
                <Card title={t('preview')} subtitle={t('previewSubtitle')} className="w-100">
                  <Ui.PreviewContainer>
                    <Ui.PreviewIframe
                      key={iFrameKey}
                      ref={previewRef}
                      src={widgetUrl}
                      title="Widget Preview"
                      scrolling="no"
                    />
                  </Ui.PreviewContainer>
                </Card>
              )}
            </Ui.ContentWrapper>
          );
        }}
      </Form>

      <Ui.TypeSelectModal
        open={isTypeModalOpen}
        onClose={handleCloseTypeModal}
        header={{ title: t('selectWidgetType') }}
        footer={() => (
          <ModalFooter
            onCancel={handleCloseTypeModal}
            onSave={handleConfirmType}
            cancelText={tApp('cancel')}
            saveText={tApp('confirm')}
            isSaveDisabled={!pendingTypeKey}
          />
        )}
      >
        <Ui.TypeSelectModalBody>
          {Object.entries(WIDGET_TYPES).map(([key, widgetType]) => {
            const Icon = WIDGET_ICONS[key];
            return (
              <Ui.TypeCard
                key={key}
                role="button"
                tabIndex={0}
                $selected={pendingTypeKey === key}
                onClick={() => setPendingTypeKey(key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPendingTypeKey(key);
                  }
                }}
              >
                <Ui.TypeCardIcon>{Icon && <Icon size={28} />}</Ui.TypeCardIcon>
                <Ui.TypeCardInfo>
                  <Ui.TypeCardLabel>{t(widgetType.titleKey)}</Ui.TypeCardLabel>
                  <Ui.TypeCardDescription>{t(widgetType.descriptionKey)}</Ui.TypeCardDescription>
                </Ui.TypeCardInfo>
              </Ui.TypeCard>
            );
          })}
        </Ui.TypeSelectModalBody>
      </Ui.TypeSelectModal>
    </Page>
  );
};

export default WidgetEditPage;
